/**
 * The Data tab's "Restore points" (vfs-service `/data/restore-points`, `/data/backups`): the project's data saved at
 * earlier points — hourly, saved by someone, taken before a change, brought from the nightly backup — newest first, with
 * the storage they use and the policy that keeps them. Picking one shows what it holds, store by store. An editor can
 * save one now, delete one, or restore all its stores (or one) after typing the project's name; a "Before restoring"
 * point is taken first, so a restore can be undone the same way. The nightly backups come after, as older entries: "Use
 * this backup" turns one into a restore point, which is then restored like any other. A viewer sees it all, read-only.
 *
 * SaveRestorePointDialog and the project-name gate are shared with the project Home (home/MoreAboutProject).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowCounterClockwise, ArrowLeft, CheckCircle, ClockCounterClockwise, CloudArrowDown, FloppyDisk, Trash, WarningCircle } from '@phosphor-icons/react';
import { ConfirmDialog, DataDialog, EmptyState, ErrorState, Loading, ReadOnlyBadge, errorText } from './DataCommon';
import {
  deleteRestorePoint, importBackup, listBackups, listRestorePoints, restoreRestorePoint, saveRestorePoint,
} from '../../services/DataService';
import { readProjectMetadata } from '../../utils/vfsClient';
import { ago, formatBytes } from '../../utils/dataModel';
import {
  absoluteTime, budgetShare, budgetText, newestFirst, objectCountText, pointDetail, pointKindLabel, pointStoresText, policyText,
  sameProjectName, storeName, unavailableText,
} from '../../utils/restorePointsModel';

const KIND_ICONS = { auto: ClockCounterClockwise, manual: FloppyDisk, 'pre-change': ArrowCounterClockwise, backup: CloudArrowDown };

export function PointKindIcon({ kind, size = 16 }) {
  const Icon = KIND_ICONS[kind] || ClockCounterClockwise;
  return <Icon aria-hidden className={`data-rp-icon is-${kind || 'auto'}`} size={size} />;
}

PointKindIcon.propTypes = { kind: PropTypes.string, size: PropTypes.number };

/** "Save a restore point": a name, then `onSaved(point)`. */
export function SaveRestorePointDialog({ projectId, onClose, onSaved }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const save = async (event) => {
    event?.preventDefault();
    if (!name.trim() || busy) { return; }
    setBusy(true);
    setError(null);
    try {
      const answer = await saveRestorePoint(projectId, name.trim());
      onSaved?.(answer?.point || null);
      onClose();
    } catch (e) {
      setError(e?.code === 'nothing' || e?.body?.code === 'nothing' ? 'There is no data to save yet: start a service that uses a datastore first.' : errorText(e));
      setBusy(false);
    }
  };
  return (
    <DataDialog
      footer={(
        <>
          <span className="data-grow" />
          <button className="data-btn" onClick={onClose} type="button">Cancel</button>
          <button className="data-btn is-primary" disabled={!name.trim() || busy} form="data-rp-save" type="submit">{busy ? 'Saving…' : 'Save'}</button>
        </>
      )}
      onClose={onClose}
      subtitle="The data of every datastore of the project as it is now, Kafka topics and their messages included."
      testId="data-rp-save-dialog"
      title="Save a restore point"
    >
      <form className="data-stack" id="data-rp-save" onSubmit={save}>
        <label className="data-label" htmlFor="data-rp-name">Name</label>
        <input
          autoComplete="off"
          className="data-input is-wide"
          data-autofocus
          disabled={busy}
          id="data-rp-name"
          maxLength={80}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Before the price import"
          type="text"
          value={name}
        />
        {error && <p className="data-error-text" role="alert">{error}</p>}
      </form>
    </DataDialog>
  );
}

SaveRestorePointDialog.propTypes = { onClose: PropTypes.func.isRequired, onSaved: PropTypes.func, projectId: PropTypes.string.isRequired };

/** Restore all the point's stores or one, after the person types the project's name; then what happened per store. */
export function RestoreDialog({ projectId, projectName, point, onClose, onRestored }) {
  const stores = point.stores || [];
  const [scope, setScope] = useState('all');
  const [one, setOne] = useState(stores[0]?.store || '');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const chosen = scope === 'all' ? stores.map((s) => s.store) : [one];
  const shownName = projectName || projectId;
  const matches = sameProjectName(typed, shownName) || sameProjectName(typed, projectId);

  const run = async (event) => {
    event?.preventDefault();
    if (!matches || busy || !chosen.length) { return; }
    setBusy(true);
    setError(null);
    try {
      const answer = await restoreRestorePoint(projectId, point.id, { stores: scope === 'all' ? undefined : chosen, confirm: typed.trim() });
      setResult(answer || {});
      onRestored?.(answer || {});
    } catch (e) {
      setError(e?.code === 'confirm_mismatch' ? 'That is not the project’s name: type it exactly as it is shown.' : errorText(e));
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    const restored = result.restored || [];
    return (
      <DataDialog
        footer={<><span className="data-grow" /><button className="data-btn is-primary" data-autofocus onClick={onClose} type="button">Done</button></>}
        onClose={onClose}
        testId="data-rp-restore-dialog"
        title="Restore finished"
      >
        <ul className="data-rp-results" data-testid="data-rp-results">
          {restored.map((r) => (
            <li className={r.ok ? 'is-ok' : 'is-bad'} key={r.store}>
              {r.ok ? <CheckCircle aria-hidden size={16} /> : <WarningCircle aria-hidden size={16} />}
              <span><strong>{storeName(r.store)}</strong>{r.ok ? ': restored' : `: not restored${r.error ? ` (${r.error})` : ''}`}</span>
            </li>
          ))}
        </ul>
        {result.before
          ? <p className="data-footnote">The data as it was just before is saved as the restore point “{result.before.name || 'Before restoring'}”: restore it to undo this.</p>
          : <p className="data-footnote">No “Before restoring” point could be saved first.</p>}
        {result.restarted?.length > 0 && <p className="data-footnote">The running services restarted to pick up the data.</p>}
      </DataDialog>
    );
  }

  return (
    <DataDialog
      footer={(
        <>
          <span className="data-grow" />
          <button className="data-btn" onClick={onClose} type="button">Cancel</button>
          <button className="data-btn is-danger-solid" data-testid="data-rp-restore-confirm" disabled={!matches || busy || !chosen.length} form="data-rp-restore" type="submit">
            {busy ? 'Restoring…' : scope === 'all' ? 'Restore the data' : `Restore ${storeName(one)}`}
          </button>
        </>
      )}
      onClose={onClose}
      subtitle={`${pointKindLabel(point.kind)} · ${absoluteTime(point.createdAt)}`}
      testId="data-rp-restore-dialog"
      title="Restore this point?"
    >
      <form className="data-stack" id="data-rp-restore" onSubmit={run}>
        {stores.length > 1 && (
          <fieldset className="data-rp-scope">
            <legend className="data-label">What to restore</legend>
            <label className="data-record-check">
              <input checked={scope === 'all'} name="data-rp-scope" onChange={() => setScope('all')} type="radio" />
              All stores in this point ({pointStoresText(point)})
            </label>
            <label className="data-record-check">
              <input checked={scope === 'one'} name="data-rp-scope" onChange={() => setScope('one')} type="radio" />
              Only one store
            </label>
            {scope === 'one' && (
              <select aria-label="Store to restore" className="data-input" onChange={(e) => setOne(e.target.value)} value={one}>
                {stores.map((s) => <option key={s.store} value={s.store}>{storeName(s.store)}</option>)}
              </select>
            )}
          </fieldset>
        )}
        <div className="data-check is-bad">
          <p>The current data of {chosen.length === 1 ? storeName(chosen[0]) : 'these stores'} is replaced by the data of this point. Anything written after {absoluteTime(point.createdAt)} goes away.</p>
          <p>A “Before restoring” point is saved first, so you can undo it. Running services of the project restart.</p>
          {chosen.includes('kafka') && <p>Kafka: the project’s topics are deleted and created again with this point’s messages, and its consumer groups go back to where they had read to. The running services stop while that happens.</p>}
        </div>
        <label className="data-label" htmlFor="data-rp-confirm">
          Type the project’s name, <strong className="data-mono">{shownName}</strong>, to confirm
        </label>
        <input autoComplete="off" className="data-input is-wide" data-autofocus disabled={busy} id="data-rp-confirm" onChange={(e) => setTyped(e.target.value)} type="text" value={typed} />
        {error && <p className="data-error-text" role="alert">{error}</p>}
      </form>
    </DataDialog>
  );
}

RestoreDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  onRestored: PropTypes.func,
  point: PropTypes.object.isRequired,
  projectId: PropTypes.string.isRequired,
  projectName: PropTypes.string,
};

function PointContents({ point }) {
  return (
    <div className="data-rp-contents" data-testid="data-rp-contents">
      {(point.stores || []).map((s) => (
        <section aria-label={storeName(s.store)} className="data-rp-store" key={s.store}>
          <h3>{storeName(s.store)}{s.sizeBytes ? <span className="data-muted"> · {formatBytes(s.sizeBytes)}</span> : null}</h3>
          {(s.objects || []).length > 0 ? (
            <ul className="data-rp-objects">
              {s.objects.map((o) => (
                <li key={`${o.kind}:${o.name}`}>
                  <span className="data-mono">{o.name}</span>
                  <span className="data-muted">{objectCountText(o)}</span>
                </li>
              ))}
            </ul>
          ) : <p className="data-muted data-rp-small">Empty</p>}
          {(s.warnings || []).map((w) => <p className="data-rp-small data-rp-warn" key={w}>{w}</p>)}
        </section>
      ))}
      {(point.skipped || []).length > 0 && (
        <section aria-label="Not included" className="data-rp-store is-skipped">
          <h3>Not included</h3>
          <ul className="data-rp-objects">
            {point.skipped.map((s) => (
              <li key={s.store}><span>{storeName(s.store)}</span><span className="data-muted">{s.reason}</span></li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

PointContents.propTypes = { point: PropTypes.object.isRequired };

function BackupItem({ item, readOnly, busy, onUse }) {
  return (
    <li className="data-rp-backup" data-testid="data-rp-backup">
      <div className="data-rp-row-main">
        <PointKindIcon kind="backup" />
        <span>
          <strong>From the nightly backup — {absoluteTime(item.createdAt)}</strong>
          <small>
            {ago(item.createdAt)}{item.sizeBytes ? ` · ${formatBytes(item.sizeBytes)}` : ''}
            {(item.stores || []).length > 0 ? ` · Can restore ${item.stores.map(storeName).join(', ')}` : ' · Nothing of this project can be restored from it'}
          </small>
          {(item.notRestorable || []).map((n) => <small className="data-rp-not" key={n.store}>{storeName(n.store)}: {n.reason}</small>)}
        </span>
      </div>
      {!readOnly && (item.stores || []).length > 0 && (
        <button className="data-btn is-tiny" disabled={busy} onClick={() => onUse(item)} type="button">{busy ? 'Bringing it in…' : 'Use this backup'}</button>
      )}
    </li>
  );
}

BackupItem.propTypes = { busy: PropTypes.bool, item: PropTypes.object.isRequired, onUse: PropTypes.func.isRequired, readOnly: PropTypes.bool };

export default function RestorePoints({ projectId, projectName = null, readOnly = false, onBack = null, initialPointId = null, onSelect = null }) {
  const [answer, setAnswer] = useState(null);
  const [error, setError] = useState(null);
  const [backups, setBackups] = useState(null);
  const [selectedId, setSelectedId] = useState(initialPointId);
  const [dialog, setDialog] = useState(null); // 'save' | 'restore' | 'delete'
  const [deleteState, setDeleteState] = useState({ busy: false, error: null });
  const [importing, setImporting] = useState(null);
  const [importError, setImportError] = useState(null);
  // The name to type: the one the workspace shows (the breadcrumb), else the project's metadata name, else its id:
  // never empty, so the confirmation is always possible (vfs accepts the metadata name or the id).
  const [name, setName] = useState(projectName || projectId);

  useEffect(() => {
    if (projectName) { setName(projectName); return undefined; }
    setName(projectId);
    let cancelled = false;
    readProjectMetadata(projectId).then((meta) => { if (!cancelled && meta?.name) { setName(meta.name); } }).catch(() => {});
    return () => { cancelled = true; };
  }, [projectId, projectName]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const next = await listRestorePoints(projectId);
      setAnswer(next);
      return next;
    } catch (e) {
      setError(errorText(e));
      return null;
    }
  }, [projectId]);

  const loadBackups = useCallback(async () => {
    try {
      setBackups(await listBackups(projectId));
    } catch (e) {
      setBackups({ available: false, reason: errorText(e), items: [] });
    }
  }, [projectId]);

  useEffect(() => { load(); loadBackups(); }, [load, loadBackups]);

  const points = useMemo(() => newestFirst(answer?.points || []), [answer]);
  const selected = points.find((p) => p.id === selectedId) || null;
  // The point shown, once the list is read (a point asked for that is gone: none), for the URL (`&restore=<id>`).
  const shownId = answer ? selected?.id || null : undefined;
  useEffect(() => { if (shownId !== undefined) { onSelect?.(shownId); } }, [shownId]); // eslint-disable-line react-hooks/exhaustive-deps

  const afterSave = async (point) => {
    await load();
    if (point?.id) { setSelectedId(point.id); }
  };
  const runDelete = async () => {
    setDeleteState({ busy: true, error: null });
    try {
      await deleteRestorePoint(projectId, selected.id);
      setDeleteState({ busy: false, error: null });
      setDialog(null);
      setSelectedId(null);
      load();
    } catch (e) {
      setDeleteState({ busy: false, error: errorText(e) });
    }
  };
  const takeBackup = async (item) => {
    setImporting(item.id);
    setImportError(null);
    try {
      const imported = await importBackup(projectId, item.id);
      await load();
      if (imported?.point?.id) { setSelectedId(imported.point.id); }
    } catch (e) {
      setImportError(errorText(e));
    } finally {
      setImporting(null);
    }
  };

  const head = (
    <div className="data-view-head">
      {onBack && <button aria-label="Back to the datastores" className="data-btn is-icon" onClick={onBack} type="button"><ArrowLeft aria-hidden size={16} /></button>}
      <h1>Restore points</h1>
      {readOnly && <ReadOnlyBadge reason="You can view this project only: someone with editor access can save or restore points" />}
      <span className="data-grow" />
      {!readOnly && answer?.available && (
        <button className="data-btn is-primary" data-testid="data-rp-save" onClick={() => setDialog('save')} type="button">
          <FloppyDisk aria-hidden size={15} />Save a restore point
        </button>
      )}
    </div>
  );

  let body;
  if (error && !answer) {
    body = <ErrorState error={error} onRetry={load} />;
  } else if (!answer) {
    body = <Loading label="Loading the restore points…" />;
  } else if (!answer.available) {
    body = <EmptyState title="Restore points are not available here">{unavailableText(answer)}</EmptyState>;
  } else {
    const share = budgetShare(answer.budget);
    body = (
      <>
        <div className="data-card data-rp-budget" data-testid="data-rp-budget">
          <div>
            <strong>{budgetText(answer.budget) || 'Storage'}</strong>
            {share !== null && <div aria-label={`${share}% of the space for restore points used`} className="data-rp-meter" role="img"><span style={{ width: `${share}%` }} /></div>}
          </div>
          <p className="data-muted">{policyText(answer.policy)} Points taken before a change and saved ones are kept too, oldest dropped first when space runs out.</p>
        </div>
        <div className="data-rp-split">
          <div className="data-card data-rp-list-card">
            <h2 className="data-card-head">Points <span className="data-group-count">{points.length}</span></h2>
            {points.length === 0 ? (
              <p className="data-muted data-rp-pad">No restore points yet. The first hourly one is taken within the hour{readOnly ? '' : ', or save one now'}.</p>
            ) : (
              <ul aria-label="Restore points" className="data-rp-list">
                {points.map((p) => {
                  const detail = pointDetail(p);
                  const on = p.id === selectedId;
                  return (
                    <li key={p.id}>
                      <button aria-current={on ? 'true' : undefined} className={`data-rp-row${on ? ' is-on' : ''}`} data-kind={p.kind} data-testid="data-rp-point" onClick={() => setSelectedId(p.id)} type="button">
                        <span className="data-rp-row-main">
                          <PointKindIcon kind={p.kind} />
                          <span>
                            <strong>{pointKindLabel(p.kind)}</strong>
                            {detail && <span className="data-rp-detail">{detail}</span>}
                            <small title={absoluteTime(p.createdAt)}>{ago(p.createdAt)} · {absoluteTime(p.createdAt)}{p.sizeBytes ? ` · ${formatBytes(p.sizeBytes)}` : ''}</small>
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            {backups && (
              <div className="data-rp-backups" data-testid="data-rp-backups">
                <h3 className="data-group-label">Older: nightly backups</h3>
                {backups.available ? (
                  (backups.items || []).length > 0 ? (
                    <ul className="data-rp-list">
                      {backups.items.map((item) => <BackupItem busy={importing === item.id} item={item} key={item.id} onUse={takeBackup} readOnly={readOnly} />)}
                    </ul>
                  ) : <p className="data-muted data-rp-pad">No nightly backups yet.</p>
                ) : <p className="data-muted data-rp-pad" data-testid="data-rp-backups-reason">{backups.reason || 'Nightly backups are not available here.'}</p>}
                {importError && <p className="data-error-text data-rp-pad" role="alert">{importError}</p>}
              </div>
            )}
          </div>
          <div className="data-card data-rp-detail-card" data-testid="data-rp-detail">
            {selected ? (
              <>
                <div className="data-card-head">
                  <PointKindIcon kind={selected.kind} />
                  <h2>{pointKindLabel(selected.kind)}</h2>
                  <span className="data-muted">{absoluteTime(selected.createdAt)}</span>
                  <span className="data-grow" />
                  {!readOnly && (
                    <>
                      <button className="data-btn is-ghost is-icon" aria-label="Delete this restore point" onClick={() => { setDeleteState({ busy: false, error: null }); setDialog('delete'); }} type="button">
                        <Trash aria-hidden size={15} />
                      </button>
                      <button className="data-btn is-danger" data-testid="data-rp-restore" disabled={!(selected.stores || []).length} onClick={() => setDialog('restore')} type="button">
                        <ArrowCounterClockwise aria-hidden size={15} />Restore…
                      </button>
                    </>
                  )}
                </div>
                {pointDetail(selected) && <p className="data-rp-pad data-rp-detail-line">{pointDetail(selected)}</p>}
                <PointContents point={selected} />
              </>
            ) : (
              <EmptyState title="Pick a restore point">What it holds shows here: each store with its tables, collections, buckets or keys and how many rows.</EmptyState>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="data-view data-rp" data-testid="restore-points">
      {head}
      {body}
      {dialog === 'save' && <SaveRestorePointDialog onClose={() => setDialog(null)} onSaved={afterSave} projectId={projectId} />}
      {dialog === 'restore' && selected && (
        <RestoreDialog onClose={() => setDialog(null)} onRestored={() => load()} point={selected} projectId={projectId} projectName={name} />
      )}
      {dialog === 'delete' && selected && (
        <ConfirmDialog busy={deleteState.busy} error={deleteState.error} onClose={() => setDialog(null)} onConfirm={runDelete} title="Delete this restore point?">
          <p>{pointKindLabel(selected.kind)} of {absoluteTime(selected.createdAt)}. The data itself is not changed.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

RestorePoints.propTypes = {
  initialPointId: PropTypes.string,
  onBack: PropTypes.func,
  // (pointId | null) => void: the point shown, once the list is read.
  onSelect: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  projectName: PropTypes.string,
  readOnly: PropTypes.bool,
};
